import axios from "axios";
import { BASE_URL, API_KEY, getAxiosConfig } from "../components/interface/Config";
import { UserData } from "../components/interface/providerinterface";
import { apiHandler } from "./authService";

export const fetchUserInfo = async () => {
  try {
    const authToken = sessionStorage.getItem('authToken'); // Get token from sessionStorage
    if (!authToken) {
      throw new Error("No authorization token found");
    }

    // const response = await axios.get(`${BASE_URL}/users/info`, getAxiosConfig());
    const url       = `${BASE_URL}/users/info`;
    const response  = await apiHandler(authToken, 'GET', {}, url);
    if (!response) {
      throw new Error("No Data");
    }
    return response.data;
  } catch (error) {
    console.error("Error fetching user info:", error);
    throw error;
  }
};

const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];

export const getNormalizedAvailabilities = (availabilities: any[]) => {
  const list = Array.isArray(availabilities) ? availabilities : [];
  let tz = "UTC";
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (e) {}

  const existingWithTz = list.find((a: any) => a.tz);
  if (existingWithTz) {
    tz = existingWithTz.tz;
  }

  return DAYS_OF_WEEK.map((dayName) => {
    const existing = list.find((a: any) => a.text === dayName);
    if (existing) {
      return {
        ...existing,
        timing: {
          slot: existing.timing?.slot || [],
          queue: existing.timing?.queue || []
        }
      };
    }
    return {
      tz,
      day: false,
      text: dayName,
      timing: {
        slot: [],
        queue: []
      }
    };
  });
};

export const fetchProviderInfo = async (userId?: number, cdate?: string) => {
  try {
    const authToken = sessionStorage.getItem('authToken');
    if (!authToken) {
      throw new Error("No authorization token found");
    }

    const url = `${BASE_URL}/users/providerinfo?user_id=${userId || ""}${cdate ? `&cdate=${cdate}` : ""}`;
    const response = await apiHandler(authToken, 'GET', {}, url);
    if (!response) {
      throw new Error("No Data");
    }

    // Normalize availabilities if response contains provider data
    if (response.data?.data) {
      response.data.data.availabilities = getNormalizedAvailabilities(response.data.data.availabilities);
    }

    return response.data;
  } catch (error) {
    console.error("Error fetching provider info:", error);
    throw error;
  }
};

/**
 * Updates an existing provider record via PATCH.
 * Specifically used for updating weekly availability slots.
 * @param providerId - The provider's record ID.
 * @param payload    - The patch payload (e.g., availabilities array).
 */
export const updateProvider = async (providerId: number, payload: any) => {
  try {
    const authToken = sessionStorage.getItem("authToken");
    if (!authToken) throw new Error("No authorization token found");

    const url = `${BASE_URL}/resource/providers/${providerId}`;
    const response = await apiHandler(authToken, "PATCH", payload, url);
    if (!response) throw new Error("No response from server");
    return response.data;
  } catch (error) {
    console.error("Error updating provider:", error);
    throw error;
  }
};

/**
 * Patches a custom unavailability block for a provider.
 * @param providerId - The provider record id (not user_id).
 * @param payload    - The unavailability data to submit.
 */
export const patchProviderUnavailability = async (
  providerId: number,
  payload: {
    from_date: string;
    to_date: string;
    from_time: string;
    to_time: string;
    reason: string;
    is_full_day: boolean;
  }
) => {
  try {
    const authToken = sessionStorage.getItem("authToken");
    if (!authToken) throw new Error("No authorization token found");

    const url = `${BASE_URL}/resource/provider_unavailabilities`;
    
    // Generate dates array from from_date to to_date in a timezone-safe way using Date.UTC
    const dates: string[] = [];
    const [startYear, startMonth, startDay] = payload.from_date.split("-").map(Number);
    const [endYear, endMonth, endDay] = payload.to_date.split("-").map(Number);
    
    let currentDate = new Date(Date.UTC(startYear, startMonth - 1, startDay));
    const endDate = new Date(Date.UTC(endYear, endMonth - 1, endDay));
    
    while (currentDate <= endDate) {
      dates.push(currentDate.toISOString().split("T")[0]);
      currentDate.setUTCDate(currentDate.getUTCDate() + 1);
    }
    
    const timingData = payload.is_full_day ? null : { 
      from_time: payload.from_time, 
      to_time: payload.to_time 
    };

    // Send API request for each date in the range
    const requests = dates.map(date => {
      const body = {
        provider_id: providerId,
        from_date: date,
        available_type: payload.reason || "leave",
        available_status: 1,
        timing: timingData
      };
      return apiHandler(authToken, "POST", body, url);
    });

    const responses = await Promise.all(requests);
    if (responses.some(r => !r)) throw new Error("No response from server for one or more dates");
    return responses[0]?.data;
  } catch (error) {
    console.error("Error patching provider unavailability:", error);
    throw error;
  }
};

/**
 * Fetches all unavailability blocks for the current provider.
 */
export const fetchProviderUnavailabilities = async () => {
  try {
    const authToken = sessionStorage.getItem("authToken");
    if (!authToken) throw new Error("No authorization token found");

    const url = `${BASE_URL}/resource/provider_unavailabilities`;
    const response = await apiHandler(authToken, "GET", {}, url);
    if (!response) throw new Error("No response from server");
    return response.data;
  } catch (error) {
    console.error("Error fetching provider unavailabilities:", error);
    throw error;
  }
};

/**
 * Deletes a specific unavailability block.
 * @param unavailabilityId - The database ID of the unavailability record.
 */
export const deleteProviderUnavailability = async (unavailabilityId: number) => {
  try {
    const authToken = sessionStorage.getItem("authToken");
    if (!authToken) throw new Error("No authorization token found");

    const url = `${BASE_URL}/resource/provider_unavailabilities/${unavailabilityId}`;
    const response = await apiHandler(authToken, "DELETE", {}, url);
    if (!response) throw new Error("No response from server");
    return response.data;
  } catch (error) {
    console.error("Error deleting provider unavailability:", error);
    throw error;
  }
};

export const fetchMasters = async () => {
  try {
    const authToken = sessionStorage.getItem('authToken'); // Get token from sessionStorage
    if (!authToken) {
      throw new Error("No authorization token found");
    }
    const url       = `${BASE_URL}/resource/masters/all?slug=country&order_by=name&dir=1`;
    const response  = await apiHandler(authToken, 'GET', {}, url);
    if (!response) {
      throw new Error("No Data");
    }
    return response.data;

    // const response = await axios.get(
    //   `${BASE_URL}/resource/masters/all?slug=country&order_by=name&dir=1`,
    //   getAxiosConfig()
    // );

  } catch (error) {
    console.error("Error fetching masters:", error);
    throw error;
  }
};

export const fetchTimezones = async () => {
  try {
    const authToken = sessionStorage.getItem('authToken'); // Get token from sessionStorage
    if (!authToken) {
      throw new Error("No authorization token found");
    }

    const url       = `${BASE_URL}/resource/timezone/all`;
    const response  = await apiHandler(authToken, 'GET', {}, url);
    if (!response) {
      throw new Error("No Data");
    }
    return response.data;

    // const response = await axios.get(`${BASE_URL}/resource/timezone/all`, getAxiosConfig());
    // return response.data;
  } catch (error) {
    console.error("Error fetching timezones:", error);
    throw error;
  }
};


/**
 * Updates the user information.
 * @param userId - The ID of the user to update.
 * @returns The updated user data.
 */

export const updateUser = async (userId: number, payload: any) => {
  try {
    const authToken = sessionStorage.getItem("authToken");
    if (!authToken) {
      throw new Error("No authorization token found");
    }

    console.log("Sending Payload:", payload);  // Add this line to log payload

    const url       = `${BASE_URL}/resource/users/${userId}`;
    const response  = await apiHandler(authToken, 'PUT',payload, url);

    if (!response) {
        throw new Error("No response received from the server");
    }

    // const response = await axios.put(
    //   `${BASE_URL}/resource/users/${userId}`,
    //   payload,
    //   getAxiosConfig()
    // );

    return response.data;
  } catch (error) {
    console.error("Error updating user:", error);
    throw error;
  }
};

/**
 * Uploads a profile image for a user via multipart/form-data.
 * @param userId - The ID of the user.
 * @param file   - The image File object to upload.
 */
export const uploadProfileImage = async (userId: number, file: File): Promise<string> => {
  const authToken = sessionStorage.getItem("authToken");
  if (!authToken) throw new Error("No authorization token found");

  const formData = new FormData();
  formData.append("profile_image", file);

  const response = await axios.put(
    `${BASE_URL}/resource/users/${userId}`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${authToken}`,
        "X-API-Key": API_KEY,
        // Do NOT set Content-Type; axios will set multipart/form-data with boundary automatically
      },
    }
  );

  // Return the new profile image URL from the response
  const imageUrl =
    response.data?.data?.profile_image ||
    response.data?.profile_image ||
    "";
  return imageUrl;
};


